import { useEffect, useReducer } from "react";
import { UI_TEXT } from "../constants";
import {
	MENU_OPTIONS,
	MODE_NAMES,
	performFuelCalculations,
	performSpeedTimeDistance,
	performUnitConversion,
	performWeightBalance,
	solveWindTriangle,
} from "../constants/calculations";
import type {
	CalculationMode,
	MathOperation,
	UnitConversionType,
	WindFlowState,
} from "../types";

interface E6BCalculatorState {
	display: string;
	mode: CalculationMode;
	subMode: UnitConversionType;
	input: string;
	result: string;
	previousValue: string;
	operation: MathOperation;
	memory: number;
	isTimerRunning: boolean;
	timerSeconds: number;
	selectedIndex: number;
	windFlow: WindFlowState;
}

type E6BCalculatorAction =
	| { type: "SET_DISPLAY"; payload: string }
	| { type: "SET_MODE"; payload: CalculationMode }
	| { type: "SET_SUB_MODE"; payload: UnitConversionType }
	| { type: "SET_INPUT"; payload: string }
	| { type: "SET_RESULT"; payload: string }
	| { type: "SET_PREVIOUS_VALUE"; payload: string }
	| { type: "SET_OPERATION"; payload: MathOperation }
	| { type: "SET_MEMORY"; payload: number }
	| { type: "SET_TIMER_RUNNING"; payload: boolean }
	| { type: "SET_TIMER_SECONDS"; payload: number }
	| { type: "INCREMENT_TIMER" }
	| { type: "SET_SELECTED_INDEX"; payload: number }
	| { type: "CLEAR_ALL" }
	| { type: "CLEAR_INPUT_RESULT" }
	| { type: "APPEND_INPUT"; payload: string }
	| { type: "BACKSPACE_INPUT" }
	| { type: "TOGGLE_SIGN" }
	| { type: "ADD_DECIMAL" }
	| { type: "UPDATE_MEMORY"; payload: (prev: number) => number }
	| { type: "SET_WIND_FLOW"; payload: WindFlowState };

const initialState: E6BCalculatorState = {
	display: UI_TEXT.INITIAL_DISPLAY,
	mode: "unit_conversions",
	subMode: "altitude",
	input: "",
	result: "",
	previousValue: "",
	operation: null,
	memory: 0,
	isTimerRunning: false,
	timerSeconds: 0,
	selectedIndex: 0,
	windFlow: { active: false, target: "ground_speed", step: 1 },
};

const e6bReducer = (
	state: E6BCalculatorState,
	action: E6BCalculatorAction,
): E6BCalculatorState => {
	switch (action.type) {
		case "SET_DISPLAY":
			return { ...state, display: action.payload };
		case "SET_MODE":
			return { ...state, mode: action.payload };
		case "SET_SUB_MODE":
			return { ...state, subMode: action.payload };
		case "SET_INPUT":
			return { ...state, input: action.payload };
		case "SET_RESULT":
			return { ...state, result: action.payload };
		case "SET_PREVIOUS_VALUE":
			return { ...state, previousValue: action.payload };
		case "SET_OPERATION":
			return { ...state, operation: action.payload };
		case "SET_MEMORY":
			return { ...state, memory: action.payload };
		case "SET_TIMER_RUNNING":
			return { ...state, isTimerRunning: action.payload };
		case "SET_TIMER_SECONDS":
			return { ...state, timerSeconds: action.payload };
		case "INCREMENT_TIMER":
			return { ...state, timerSeconds: state.timerSeconds + 1 };
		case "SET_SELECTED_INDEX":
			return { ...state, selectedIndex: action.payload };
		case "CLEAR_ALL":
			return {
				...state,
				input: "",
				result: "",
				previousValue: "",
				operation: null,
			};
		case "CLEAR_INPUT_RESULT":
			return { ...state, input: "", result: "" };
		case "APPEND_INPUT":
			return { ...state, input: state.input + action.payload };
		case "BACKSPACE_INPUT":
			return { ...state, input: state.input.slice(0, -1) };
		case "TOGGLE_SIGN":
			return {
				...state,
				input: state.input.startsWith("-")
					? state.input.slice(1)
					: `-${state.input}`,
			};
		case "ADD_DECIMAL":
			return {
				...state,
				input: state.input === "" ? UI_TEXT.DECIMAL_POINT : `${state.input}.`,
			};
		case "UPDATE_MEMORY":
			return { ...state, memory: action.payload(state.memory) };
		case "SET_WIND_FLOW":
			return { ...state, windFlow: action.payload };
		default:
			return state;
	}
};

export const useE6BCalculator = () => {
	const [state, dispatch] = useReducer(e6bReducer, initialState);

	useEffect(() => {
		let interval: ReturnType<typeof setInterval> | null = null;
		if (state.isTimerRunning) {
			interval = setInterval(() => {
				dispatch({ type: "INCREMENT_TIMER" });
			}, 1000);
		}
		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [state.isTimerRunning]);

	const handleUnitOperations = (op: string) => {
		switch (op) {
			case "SET_UNIT": {
				if (state.mode !== "unit_conversions") {
					return;
				}
				const currentMenuItems = MENU_OPTIONS[state.mode];
				if (currentMenuItems) {
					const nextIndex = (state.selectedIndex + 1) % currentMenuItems.length;
					dispatch({ type: "SET_SELECTED_INDEX", payload: nextIndex });
					dispatch({
						type: "SET_SUB_MODE",
						payload: currentMenuItems[nextIndex].type as UnitConversionType,
					});
					dispatch({ type: "CLEAR_INPUT_RESULT" });
				}
				break;
			}
			case "CONV_UNIT": {
				if (state.mode !== "unit_conversions") {
					return;
				}
				if (state.input) {
					performCalculation();
				}
				break;
			}
			default: {
				return;
			}
		}
	};

	const handleNumberPress = (num: string) => {
		if (state.input.length >= 10) {
			return;
		}

		if (state.result) {
			dispatch({ type: "SET_RESULT", payload: "" });
		}

		if (state.operation && !state.input) {
			dispatch({ type: "SET_INPUT", payload: num });
		} else {
			dispatch({ type: "APPEND_INPUT", payload: num });
		}
	};

	const handleMathOperation = (op: string) => {
		if (state.input) {
			if (state.previousValue && state.operation) {
				const r = performMathCalculation();
				if (r === null) {
					return;
				}
				dispatch({ type: "SET_PREVIOUS_VALUE", payload: r?.toString() ?? "" });
			} else {
				dispatch({ type: "SET_PREVIOUS_VALUE", payload: state.input });
			}
			dispatch({ type: "SET_OPERATION", payload: op as MathOperation });
			dispatch({ type: "SET_INPUT", payload: "" });
		}
	};

	const performMathCalculation = () => {
		if (!(state.previousValue && state.input && state.operation)) {
			return null;
		}

		const prev = Number.parseFloat(state.previousValue);
		const current = Number.parseFloat(state.input);

		if (Number.isNaN(prev) || Number.isNaN(current)) {
			dispatch({ type: "SET_RESULT", payload: UI_TEXT.ERROR_INVALID_NUMBER });
			return null;
		}

		let mathResult = 0;
		switch (state.operation) {
			case "+": {
				mathResult = prev + current;
				break;
			}
			case "-": {
				mathResult = prev - current;
				break;
			}
			case "×": {
				mathResult = prev * current;
				break;
			}
			case "÷": {
				if (current === 0) {
					dispatch({
						type: "SET_RESULT",
						payload: UI_TEXT.ERROR_DIVISION_BY_ZERO,
					});
					return null;
				}
				mathResult = prev / current;
				break;
			}
			default: {
				return null;
			}
		}

		if (!Number.isFinite(mathResult)) {
			dispatch({ type: "SET_RESULT", payload: UI_TEXT.ERROR_INVALID_RESULT });
			return;
		}

		dispatch({
			type: "SET_INPUT",
			payload: mathResult.toString(),
		});
		dispatch({
			type: "SET_RESULT",
			payload: mathResult.toString(),
		});
		dispatch({ type: "SET_PREVIOUS_VALUE", payload: "" });
		dispatch({ type: "SET_OPERATION", payload: null });
		return mathResult;
	};

	const handleOperatorPress = (op: string) => {
		switch (op) {
			case "BACK": {
				if (state.mode !== "unit_conversions") {
					dispatch({ type: "SET_MODE", payload: "unit_conversions" });
					dispatch({
						type: "SET_DISPLAY",
						payload: MODE_NAMES.unit_conversions,
					});
					dispatch({ type: "SET_SELECTED_INDEX", payload: 0 });
					dispatch({ type: "SET_SUB_MODE", payload: "altitude" });
					dispatch({ type: "CLEAR_INPUT_RESULT" });
				} else {
					dispatch({ type: "CLEAR_INPUT_RESULT" });
				}
				break;
			}
			case "C": {
				dispatch({ type: "CLEAR_ALL" });
				break;
			}
			case "BKSP": {
				dispatch({ type: "BACKSPACE_INPUT" });
				break;
			}
			case "=": {
				if (state.operation) {
					performMathCalculation();
				} else {
					performCalculation();
				}
				break;
			}
			case ".": {
				if (!state.input.includes(".") && state.input.length < 9) {
					dispatch({ type: "ADD_DECIMAL" });
				}
				break;
			}
			case "+/-": {
				if (state.input && state.input !== "0") {
					dispatch({ type: "TOGGLE_SIGN" });
				}
				break;
			}
			default: {
				return;
			}
		}
	};

	const handleMemoryOperations = (op: string) => {
		const currentValue = Number.parseFloat(state.input) || 0;
		switch (op) {
			case "M": {
				dispatch({ type: "SET_INPUT", payload: state.memory.toString() });
				break;
			}
			case "M+": {
				dispatch({
					type: "UPDATE_MEMORY",
					payload: (prev: number) => prev + currentValue,
				});
				break;
			}
			case "M-": {
				dispatch({
					type: "UPDATE_MEMORY",
					payload: (prev: number) => prev - currentValue,
				});
				break;
			}
			case "MC": {
				dispatch({ type: "SET_MEMORY", payload: 0 });
				break;
			}
			default: {
				return;
			}
		}
	};

	const handleTimer = () => {
		if (state.isTimerRunning) {
			dispatch({ type: "SET_TIMER_RUNNING", payload: false });
		} else {
			dispatch({ type: "SET_TIMER_RUNNING", payload: true });
			dispatch({ type: "SET_TIMER_SECONDS", payload: 0 });
		}
	};

	// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: <explanation>
	const performCalculation = () => {
		if (!state.input) {
			return;
		}

		const value = Number.parseFloat(state.input);
		if (Number.isNaN(value)) {
			return;
		}

		let calculatedResult = "";

		switch (state.mode) {
			case "unit_conversions": {
				calculatedResult = performUnitConversion(value, state.subMode);
				break;
			}
			case "speed_time_distance": {
				calculatedResult = performSpeedTimeDistance(value, state.selectedIndex);
				break;
			}
			case "fuel_calculations": {
				calculatedResult = performFuelCalculations(value, state.selectedIndex);
				break;
			}
			case "wind_triangle": {
				const item = MENU_OPTIONS.wind_triangle[state.selectedIndex];
				if (!item) {
					break;
				}

				const v = value;
				const wf = state.windFlow;

				if (!wf.active) {
					dispatch({
						type: "SET_WIND_FLOW",
						payload: {
							active: true,
							target: item.type as "ground_speed" | "track",
							step: 1,
						},
					});
					dispatch({ type: "SET_RESULT", payload: "Enter TAS (kt)" });
					dispatch({ type: "SET_INPUT", payload: "" });
					return;
				}

				if (wf.step === 1) {
					dispatch({
						type: "SET_WIND_FLOW",
						payload: { ...wf, tas: v, step: 2 },
					});
					dispatch({ type: "SET_RESULT", payload: "Enter wind speed (kt)" });
					dispatch({ type: "SET_INPUT", payload: "" });
					return;
				}
				if (wf.step === 2) {
					dispatch({
						type: "SET_WIND_FLOW",
						payload: { ...wf, windSpeed: v, step: 3 },
					});
					dispatch({
						type: "SET_RESULT",
						payload: "Enter wind direction (° TRUE, from)",
					});
					dispatch({ type: "SET_INPUT", payload: "" });
					return;
				}
				if (wf.step === 3) {
					dispatch({
						type: "SET_WIND_FLOW",
						payload: { ...wf, windDir: v, step: 4 },
					});
					dispatch({ type: "SET_RESULT", payload: "Enter heading (° TRUE)" });
					dispatch({ type: "SET_INPUT", payload: "" });
					return;
				}
				if (wf.step === 4) {
					const tas = wf.tas ?? 0;
					const wv = wf.windSpeed ?? 0;
					const wd = wf.windDir ?? 0;
					const hdg = v;
					const { groundSpeed, trackTrue } = solveWindTriangle(
						tas,
						wd,
						wv,
						hdg,
					);

					const gsStr = `${groundSpeed.toFixed(0)} kt`;
					const trkStr = `${trackTrue.toFixed(0)}° true`;

					calculatedResult =
						wf.target === "ground_speed" ? `GS: ${gsStr}` : `Track: ${trkStr}`;

					dispatch({
						type: "SET_WIND_FLOW",
						payload: { active: false, target: "ground_speed", step: 1 },
					});
					break;
				}

				break;
			}
			case "weight_balance": {
				calculatedResult = performWeightBalance(value, state.selectedIndex);
				break;
			}
			default: {
				return;
			}
		}

		dispatch({ type: "SET_RESULT", payload: calculatedResult });
	};

	const handleModeChange = (newMode: CalculationMode) => {
		dispatch({ type: "SET_MODE", payload: newMode });
		dispatch({ type: "SET_DISPLAY", payload: MODE_NAMES[newMode] });
		dispatch({ type: "SET_SELECTED_INDEX", payload: 0 });
		dispatch({ type: "CLEAR_INPUT_RESULT" });
		dispatch({
			type: "SET_WIND_FLOW",
			payload: { active: false, target: "ground_speed", step: 1 },
		});
	};

	const handleNavigationPress = (direction: "up" | "down") => {
		const currentMenuItems = MENU_OPTIONS[state.mode];
		if (direction === "up" && state.selectedIndex > 0) {
			const newIndex = state.selectedIndex - 1;
			dispatch({ type: "SET_SELECTED_INDEX", payload: newIndex });
			if (state.mode === "unit_conversions") {
				dispatch({
					type: "SET_SUB_MODE",
					payload: currentMenuItems[newIndex].type as UnitConversionType,
				});
			}
		} else if (
			direction === "down" &&
			state.selectedIndex < currentMenuItems.length - 1
		) {
			const newIndex = state.selectedIndex + 1;
			dispatch({ type: "SET_SELECTED_INDEX", payload: newIndex });
			if (state.mode === "unit_conversions") {
				dispatch({
					type: "SET_SUB_MODE",
					payload: currentMenuItems[newIndex].type as UnitConversionType,
				});
			}
		}
	};

	return {
		state,
		handlers: {
			handleUnitOperations,
			handleNumberPress,
			handleMathOperation,
			handleOperatorPress,
			handleMemoryOperations,
			handleTimer,
			handleModeChange,
			handleNavigationPress,
			performCalculation,
		},
	};
};
